import { Coordinate, TileSize } from "game-common/models"
import { ItemTypeCharacteristics } from "game-common/item/item_type_characteristics"
import type { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { TileInteractionTarget, tileInteractionsAt } from "game-common/entity_tile_interactions"

/**
 * Laying a wall atop a wall: hold Shift and point at a wall, and the new wall goes on the storey above it, raising
 * that storey when it does not exist yet. Without Shift a wall stays on the player's own storey. (Picking the spot
 * on top of a wall where it is drawn crowded out the cells beside the wall: a storey rises a tile and a half, so
 * those spots covered the cells north of a wall.) A roof pointed at the top of a raised storey's walls roofs that
 * storey.
 */

/** A cell to build on, and the storey it lands on when that is not the player's own. */
export type BuildTarget = Coordinate & { level?: number }

export type WallMap = Pick<TileMapMetaProvider, "getMapDeltaAt">

/** The player's storey or one linked above it. */
export interface StoreyView {
    level: number
    map: WallMap
}

/** A target with `level` only when there is one, so a plain cell stays exactly `{ x, y }`. */
export const withLevel = (point: Coordinate, level?: number): BuildTarget =>
    level === undefined ? { x: point.x, y: point.y } : { x: point.x, y: point.y, level }

/** The same cell on the same storey. (util's isEqual compares x and y only.) */
export const sameTarget = (a?: BuildTarget, b?: BuildTarget): boolean =>
    !!a && !!b && a.x === b.x && a.y === b.y && a.level === b.level

/** A built wall stands on this cell: its base (stamped with the cell as parent), or a column piece that blocks. */
export const wallCellAt = (map: WallMap, at: Coordinate): boolean => {
    const delta = map.getMapDeltaAt?.(at.x, at.y, "sprite")
    if (!delta || delta.removed || delta.context?.previewTarget) return false
    const parent = delta.context?.parent
    if (parent && (parent.x !== at.x || parent.y !== at.y) && !delta.blocking) return false
    return ItemTypeCharacteristics[delta.context?.itemType]?.buildType === "wall"
}

/** A built roof covers this cell. */
export const roofCellAt = (map: WallMap, at: Coordinate): boolean => {
    const delta = map.getMapDeltaAt?.(at.x, at.y, "sky")
    if (!delta || delta.removed || delta.context?.previewTarget) return false
    return ItemTypeCharacteristics[delta.context?.itemType]?.buildType === "roof"
}

/** The cell under `world` on a plane drawn `yOffset` from the player's (up is negative). */
export const cellOnPlane = (world: Coordinate, yOffset: number): Coordinate => ({
    x: Math.floor(world.x / TileSize),
    y: Math.floor((world.y - yOffset) / TileSize),
})

/**
 * The walls drawn where a pointer is `fy` of the way down cell `at` (0 its top edge, 1 its bottom), the likelier first:
 * the wall standing on the cell (a column piece too: the player laid it), or the wall whose cap it is. A wall's cell can
 * also carry the top of the wall south of it (the builder parents it to that wall: a corner's top rail runs along the
 * bottom of the cell above it), and there the lower half is that wall's top, the upper half the cell's own wall. Down a
 * column of walls the posts run together, and a wall is drawn up into the cell north of it, so the wall south of a
 * wall cell comes last: with the cell's own wall built on, pointing there raises the next wall down the column.
 */
export const wallsDrawnAt = (map: WallMap, at: Coordinate, fy = 0): Coordinate[] => {
    const parent = map.getMapDeltaAt?.(at.x, at.y, "sprite")?.context?.parent
    const owner = parent && wallCellAt(map, parent) ? { x: parent.x, y: parent.y } : undefined
    if (!wallCellAt(map, at)) return owner ? [owner] : []
    const own = { x: at.x, y: at.y }
    const south = { x: at.x, y: at.y + 1 }
    if (owner && owner.x === south.x && owner.y === south.y) return fy >= 0.5 ? [owner, own] : [own, owner]
    return wallCellAt(map, south) ? [own, south] : [own]
}

/** A wall laid atop a wall: where it goes, and the cell the pointer picked it by (on the plane of the storey it stands on). */
export interface StackPick {
    target: BuildTarget
    at: Coordinate
}

/** The cell under `world` on a plane drawn `yOffset` from the player's, and how far down it the point is (0 to 1). */
const pointOnPlane = (world: Coordinate, yOffset: number): { at: Coordinate; fy: number } => {
    const at = cellOnPlane(world, yOffset)
    return { at, fy: (world.y - yOffset) / TileSize - at.y }
}

/**
 * Shift held: the storey above the wall under a pointer at `world`. Walls are picked where they are drawn, and the lowest
 * with nothing on it yet wins: a raised wall is drawn over the walls behind it on the storey below, which still take the
 * next wall first, so a house goes up a storey at a time. When every wall under the pointer is built on, the lowest,
 * which is refused. Over no wall, the storey above the player's cell under the pointer, so the cursor still shows the
 * mode and the server refuses it. `storeys` are consecutive from the player's own upward; `planeOffset(level)` is how
 * far a storey is drawn from the player's.
 */
export const stackWallPick = (world: Coordinate, storeys: StoreyView[], planeOffset: (level: number) => number): StackPick => {
    let builtOn: StackPick | undefined
    for (let index = 0; index < storeys.length; index++) {
        const { level, map } = storeys[index]
        const { at, fy } = pointOnPlane(world, planeOffset(level))
        for (const base of wallsDrawnAt(map, at, fy)) {
            const pick = { target: withLevel(base, level + 1), at }
            if (!storeys[index + 1] || !wallCellAt(storeys[index + 1].map, base)) return pick
            builtOn = builtOn ?? pick
        }
    }
    if (builtOn) return builtOn
    const at = cellOnPlane(world, planeOffset(storeys[0].level))
    return { target: withLevel(at, storeys[0].level + 1), at }
}

/** During a Shift stroke that stacks storey `level`: the wall under the pointer on the storey below, or the bare cell. */
export const stackStrokePick = (world: Coordinate, level: number, storeys: StoreyView[],
    planeOffset: (level: number) => number): StackPick => {
    const { at, fy } = pointOnPlane(world, planeOffset(level - 1))
    const below = storeys.find(storey => storey.level === level - 1)
    return { target: withLevel((below && wallsDrawnAt(below.map, at, fy)[0]) || at, level), at }
}

/**
 * A roof for a storey above the player's: on each raised storey's roof plane (its plane, lifted by `roofOffset`), from
 * the top down, the first cell over a wall or roof of that storey, so pointing at the top of a raised wall roofs that
 * storey. Undefined: the roof is for the player's own storey.
 */
export const atopRoofTarget = (world: Coordinate, storeys: StoreyView[], planeOffset: (level: number) => number,
    roofOffset: number): BuildTarget | undefined => {
    for (let index = storeys.length - 1; index >= 1; index--) {
        const { level, map } = storeys[index]
        const cell = cellOnPlane(world, planeOffset(level) + roofOffset)
        if (wallCellAt(map, cell) || roofCellAt(map, cell)) return withLevel(cell, level)
    }
    return undefined
}

/** During a stroke that paints storey `level`: the cell under the pointer on that storey's plane. */
export const storeyStrokeTarget = (world: Coordinate, level: number, planeOffset: (level: number) => number, lift = 0): BuildTarget =>
    withLevel(cellOnPlane(world, planeOffset(level) + lift), level)

/** Another storey as it is drawn right now: `yOffset` from the player's own (up is negative). */
export interface DrawnStorey extends StoreyView {
    roomId: string
    yOffset: number
}

/**
 * The tile verbs under a press at `world` while other storeys are drawn around the player's. A wall on another
 * storey is picked on its drawn plane, as when laying a wall atop one, and offers its Dismantle there. Higher
 * storeys are drawn over lower ones, so they answer first: those above the player's (`level`), then the player's
 * own (`own`), then those below.
 */
export const storeyTileTarget = (world: Coordinate, level: number, storeys: DrawnStorey[],
    own: () => TileInteractionTarget | undefined): TileInteractionTarget | undefined => {
    const wallOn = (storey: DrawnStorey): TileInteractionTarget | undefined => {
        const target = tileInteractionsAt(storey.map, cellOnPlane(world, storey.yOffset), { includeRoof: false, layerIds: ["sprite"] })
        const interactions = target?.interactions.filter(verb => verb.name === "dismantle")
        return interactions?.length ? { ...target, interactions, roomId: storey.roomId, yOffset: storey.yOffset } : undefined
    }
    const highestFirst = storeys.slice().sort((a, b) => b.level - a.level)
    for (const storey of highestFirst) {
        const target = storey.level > level && wallOn(storey)
        if (target) return target
    }
    const target = own()
    if (target) return target
    for (const storey of highestFirst) {
        const target = storey.level < level && wallOn(storey)
        if (target) return target
    }
    return undefined
}
